import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AlertTriangle, Check, ShieldAlert } from "lucide-react";

const CONFIRM_PHRASE = "DELETE MY ACCOUNT";

export default function Account() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div data-testid="account-page" className="max-w-3xl space-y-8">
      <div>
        <div className="label-meta">My account</div>
        <div className="display">Account &amp; security</div>
        <p className="text-[13px] mt-2" style={{ color: "var(--parapet-text-secondary)" }}>
          Manage your personal identity on Parapet. These changes affect only your
          login — your workspace, team and billing live under <em>Team &amp; Settings</em>.
        </p>
      </div>

      <ProfileSection user={user} onSaved={refresh} />
      <PasswordSection />
      <DangerZone user={user} onDeleted={async () => {
        try { await logout(); } catch (e) { console.warn("logout after delete failed", e?.message); }
        navigate("/login", { replace: true });
      }} />
    </div>
  );
}

/* ---------- Profile (name + email) ---------- */
function ProfileSection({ user, onSaved }) {
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const emailChanged = email.trim().toLowerCase() !== (user?.email || "").toLowerCase();

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true); setError(""); setSaved(false);
    try {
      const payload = {};
      if (fullName.trim() && fullName !== user?.full_name) payload.full_name = fullName.trim();
      if (emailChanged) {
        payload.email = email.trim().toLowerCase();
        payload.current_password = currentPassword;
      }
      if (Object.keys(payload).length === 0) {
        setSaving(false);
        return;
      }
      await api.put("/auth/me", payload);
      await onSaved?.();
      setSaved(true);
      setCurrentPassword("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="p-card" data-testid="account-profile-section">
      <h2 className="mb-1">Profile</h2>
      <p className="text-[12px] mb-4" style={{ color: "var(--parapet-text-secondary)" }}>
        Your name and login email.
      </p>
      <form onSubmit={save} className="space-y-3">
        <div>
          <label className="label-meta">Full name</label>
          <input
            className="p-input mt-1"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            data-testid="account-name-input"
          />
        </div>
        <div>
          <label className="label-meta">Email</label>
          <input
            type="email"
            className="p-input mt-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="account-email-input"
          />
          {emailChanged && (
            <p className="text-[11px] mt-1" style={{ color: "var(--parapet-text-secondary)" }}>
              Changing your email will sign you out of other devices.
            </p>
          )}
        </div>
        {emailChanged && (
          <div>
            <label className="label-meta">Current password (required to change email)</label>
            <input
              type="password"
              className="p-input mt-1"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              data-testid="account-email-current-password"
              autoComplete="current-password"
            />
          </div>
        )}
        {error && (
          <div className="text-[12px]" style={{ color: "#7a0606" }} data-testid="account-profile-error">
            {error}
          </div>
        )}
        {saved && (
          <div className="text-[12px] flex items-center gap-1" style={{ color: "#0a6b2a" }} data-testid="account-profile-saved">
            <Check size={13} /> Profile updated.
          </div>
        )}
        <button
          type="submit"
          className="btn-primary"
          disabled={saving || (emailChanged && !currentPassword)}
          data-testid="account-profile-save"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </form>
    </section>
  );
}

/* ---------- Password ---------- */
function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError(""); setDone(false);
    if (next.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setError("New passwords do not match."); return; }
    if (next === current) { setError("New password must be different from your current password."); return; }
    setSaving(true);
    try {
      await api.post("/auth/me/change-password", { current_password: current, new_password: next });
      setDone(true); setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="p-card" data-testid="account-password-section">
      <h2 className="mb-1">Change password</h2>
      <p className="text-[12px] mb-4" style={{ color: "var(--parapet-text-secondary)" }}>
        Other sessions on other devices will be signed out.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="label-meta">Current password</label>
          <input type="password" className="p-input mt-1" value={current} onChange={(e) => setCurrent(e.target.value)}
                 autoComplete="current-password" required data-testid="account-password-current" />
        </div>
        <div>
          <label className="label-meta">New password</label>
          <input type="password" className="p-input mt-1" value={next} onChange={(e) => setNext(e.target.value)}
                 autoComplete="new-password" required data-testid="account-password-new" />
        </div>
        <div>
          <label className="label-meta">Confirm new password</label>
          <input type="password" className="p-input mt-1" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                 autoComplete="new-password" required data-testid="account-password-confirm" />
        </div>
        {error && (
          <div className="text-[12px]" style={{ color: "#7a0606" }} data-testid="account-password-error">{error}</div>
        )}
        {done && (
          <div className="text-[12px] flex items-center gap-1" style={{ color: "#0a6b2a" }} data-testid="account-password-saved">
            <Check size={13} /> Password updated.
          </div>
        )}
        <button type="submit" className="btn-primary" disabled={saving} data-testid="account-password-save">
          {saving ? "Updating…" : "Update password"}
        </button>
      </form>
    </section>
  );
}

/* ---------- Danger zone (delete account) ---------- */
function DangerZone({ user, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("soft");
  const [password, setPassword] = useState("");
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const canConfirm = phrase === CONFIRM_PHRASE && password.length > 0;

  const submit = async () => {
    if (!canConfirm) return;
    setBusy(true); setError("");
    try {
      await api.delete("/auth/me", {
        data: { current_password: password, mode, confirm_phrase: phrase },
      });
      await onDeleted?.();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <section
      className="p-card"
      style={{ borderColor: "#7a0606", background: "#fff" }}
      data-testid="account-danger-section"
    >
      <div className="flex items-start gap-2">
        <ShieldAlert size={18} style={{ color: "#7a0606" }} />
        <div>
          <h2 className="mb-1" style={{ color: "#7a0606" }}>Danger zone</h2>
          <p className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>
            Permanently delete your Parapet account.
            If you're the sole owner of a workspace, transfer ownership first.
          </p>
        </div>
      </div>

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-4"
          style={{
            background: "#fff",
            color: "#7a0606",
            border: "1px solid #7a0606",
            padding: "8px 14px",
            borderRadius: 4,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
          data-testid="account-delete-open"
        >
          Delete my account…
        </button>
      ) : (
        <div className="mt-4 space-y-4">
          <div className="text-[12px] p-3" style={{ background: "#FDECEC", border: "1px solid #f7c1c1", borderRadius: 4 }}>
            <div className="flex items-center gap-1 font-medium mb-1" style={{ color: "#7a0606" }}>
              <AlertTriangle size={13} /> This sign-out is permanent.
            </div>
            <ul className="list-disc pl-4 space-y-1">
              <li>Your login will stop working immediately.</li>
              <li><strong>Soft delete</strong>: account is suspended, can be restored by an admin within 30 days.</li>
              <li><strong>Hard delete</strong>: full GDPR erasure — name, email and password are wiped permanently. <em>This cannot be undone.</em></li>
            </ul>
          </div>

          <div>
            <label className="label-meta">Deletion type</label>
            <div className="flex gap-2 mt-1">
              <button
                type="button"
                onClick={() => setMode("soft")}
                className="filter-chip"
                style={mode === "soft" ? { background: "#111", color: "#fff", borderColor: "#111" } : {}}
                data-testid="account-delete-mode-soft"
              >
                Soft delete (restorable)
              </button>
              <button
                type="button"
                onClick={() => setMode("hard")}
                className="filter-chip"
                style={mode === "hard" ? { background: "#7a0606", color: "#fff", borderColor: "#7a0606" } : {}}
                data-testid="account-delete-mode-hard"
              >
                Hard delete (GDPR erasure)
              </button>
            </div>
          </div>

          <div>
            <label className="label-meta">Confirm with your password</label>
            <input
              type="password"
              className="p-input mt-1"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="account-delete-password"
            />
          </div>

          <div>
            <label className="label-meta">
              Type <span className="value-mono">{CONFIRM_PHRASE}</span> to confirm
            </label>
            <input
              type="text"
              className="p-input mt-1"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              data-testid="account-delete-phrase"
            />
          </div>

          {error && (
            <div className="text-[12px]" style={{ color: "#7a0606" }} data-testid="account-delete-error">{error}</div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setPassword(""); setPhrase(""); setError(""); }}
              className="btn-ghost"
              data-testid="account-delete-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canConfirm || busy}
              style={{
                background: "#7a0606",
                color: "#fff",
                border: 0,
                padding: "8px 14px",
                borderRadius: 4,
                fontSize: 13,
                fontWeight: 600,
                cursor: canConfirm && !busy ? "pointer" : "not-allowed",
                opacity: canConfirm && !busy ? 1 : 0.5,
              }}
              data-testid="account-delete-confirm"
            >
              {busy ? "Deleting…" : mode === "hard" ? "Permanently delete account" : "Delete account"}
            </button>
          </div>

          <p className="text-[11px]" style={{ color: "var(--parapet-text-secondary)" }}>
            Signed in as <span className="value-mono">{user?.email}</span>.
          </p>
        </div>
      )}
    </section>
  );
}
